import { styled } from '@mui/material';
import { useAtom } from 'jotai';
import {
  selectedChecklistItemsState,
  isConfirmChecklistBarActiveState,
  isMoveChecklistModeActiveState,
  checklistTableRowSelectionState,
} from '../../../../state/UIState';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-white.svg';
import { ReactComponent as MoveIcon } from '../../../../assets/icons/corner-arrow-white.svg';
import { useCallback, useMemo, useState } from 'react';
import { ConfirmDeleteModal } from '../../../../components/ConfirmDeleteModal/ConfirmDeleteModal';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { SelectionBar } from '../../../../components/SelectionBar/SelectionBar';
import { useAppConfig, useUpdateAppConfig } from '../../../../queries/useAppConfig';
import { useUpdateChecklistItems } from '../../../../queries/useChecklistItems';

const Wrapper = styled('div')``;

export const ChecklistItemsSelectionBar = () => {
  const [selectedChecklistItems] = useAtom(selectedChecklistItemsState);
  const [, setIsConfirmMoveBarActive] = useAtom(isConfirmChecklistBarActiveState);
  const [isMoveModeActive, setIsBulkMoveModeActive] = useAtom(isMoveChecklistModeActiveState);
  const [, setRowSelection] = useAtom(checklistTableRowSelectionState);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const handleOpenConfirmDeleteModal = () => setIsConfirmDeleteModalOpen(true);
  const handleCloseConfirmDeleteModal = () => setIsConfirmDeleteModalOpen(false);
  const { data: appConfigData } = useAppConfig();
  const updateAppConfig = useUpdateAppConfig();
  const { pushSuccessToast } = useToastMessage();
  const updateChecklistItems = useUpdateChecklistItems();

  const actions = useMemo(() => {
    return [
      {
        id: 'MOVE',
        value: 'Move checklist items to...',
        icon: <MoveIcon />,
      },
      {
        id: 'DELETE',
        value: 'Delete checklist items',
        icon: <DeleteIcon />,
      },
      {
        id: 'CANCEL',
        value: 'Cancel',
      },
    ];
  }, []);

  const onBulkMove = useCallback(() => {
    setIsConfirmMoveBarActive(true);
    setIsBulkMoveModeActive(true);
  }, [setIsBulkMoveModeActive, setIsConfirmMoveBarActive]);

  const handleDeleteKpis = useCallback(async () => {
    const payload = {
      data: selectedChecklistItems.map((item) => ({
        id: item.id,
        isDeleted: true,
      })),
    };

    await updateChecklistItems.mutateAsync(payload, {
      onSuccess: async () => {
        if (!appConfigData?.data) {
          return;
        }
        await updateAppConfig.mutateAsync({
          data: { ...appConfigData?.data, areCheckListSurveysUpToDate: false },
        });
        pushSuccessToast({
          message: `${selectedChecklistItems.length} items were successfully deleted.`,
        });
      },
    });
    setRowSelection({});
    handleCloseConfirmDeleteModal();
  }, [
    appConfigData?.data,
    pushSuccessToast,
    selectedChecklistItems,
    setRowSelection,
    updateAppConfig,
    updateChecklistItems,
  ]);

  const handleSelectAction = useCallback(
    (actionId: string) => {
      if (isMoveModeActive) return;
      switch (actionId) {
        case 'MOVE':
          onBulkMove();
          return;
        case 'DELETE':
          handleOpenConfirmDeleteModal();
          return;
        case 'CANCEL':
          setRowSelection({});
          return;
        default:
          throw new Error('Unknown option');
      }
    },
    [isMoveModeActive, onBulkMove, setRowSelection]
  );
  return (
    <Wrapper>
      <SelectionBar
        actions={actions}
        isDisabled={isMoveModeActive}
        selectedItemsLength={selectedChecklistItems.length}
        isActive={selectedChecklistItems.length > 0}
        onSelectAction={handleSelectAction}
      />

      <ConfirmDeleteModal
        onClose={() => {
          handleCloseConfirmDeleteModal();
        }}
        onConfirm={handleDeleteKpis}
        title='Delete the checklist items?'
        note='These checklist items are active and used in Surveys & Company Profile. Any modifications you make will have a global effect.'
        confirmLabel='Yes, Delete'
        cancelLabel='No, Cancel'
        isOpen={isConfirmDeleteModalOpen}
      />
    </Wrapper>
  );
};
